import { inject, Injectable, signal } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { map, Observable, Subject, tap } from 'rxjs';
import { GstarClient } from 'src/app/web-api-client';
import { Collection } from '../models';
import { TaxPolicy } from '../models/tax-policy';
import { mapToTaxPoliciesFromResponse } from '../mappers/tax-policy';
import { TaxPoliciesGrid } from '../types/tax-policies.type';
import { PlainObject } from 'odata-query';
import { isEmptyObject } from '@shared/functions/is-empty-object';

@Injectable()
export class TaxPoliciesManagerService {
  private gstarClient = inject(GstarClient);
  private query = signal<TaxPoliciesGrid>({});
  private taxPolicies = new Subject<Collection<TaxPolicy>>();

  taxPolicies$ = this.taxPolicies.asObservable();

  updateTaxRules(): Observable<Collection<TaxPolicy>> {
    this.evaluate();
    const { filter, top, skip, orderBy } = this.query();
    return this.gstarClient
      .taxpoliciesGET(
        undefined,
        buildQueryFilter({ filter }),
        top?.toString(),
        skip?.toString(),
        orderBy?.toString(),
        'true',
        undefined
      )
      .pipe(
        map(response => mapToTaxPoliciesFromResponse(response)),
        tap((taxPolicies: Collection<TaxPolicy>) => {
          this.taxPolicies.next(taxPolicies);
        })
      );
  }

  updateQuery(updatedQuery: TaxPoliciesGrid): void {
    this.query.update((current: TaxPoliciesGrid) => {
      current.orderBy = updatedQuery.orderBy;

      if (isEmptyObject(<object>updatedQuery.filter)) {
        current.filter = {};
        current.skip = 0;
        current.top = 10;
      } else {
        current.top = updatedQuery.top;
        current.skip = updatedQuery.skip;
        current.filter = {
          ...(current.filter as PlainObject),
          ...(updatedQuery.filter! as PlainObject),
        };
      }

      return current;
    });

    this.updateTaxRules().subscribe();
  }

  private evaluate(): void {
    const query = this.query();
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    filters.set('isActive', true);

    if (!query.orderBy) {
      query.orderBy = 'name ASC';
    }

    query.filter = Object.fromEntries(filters);
    this.query.set(query);
  }
}
